import React, { useState, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useWorkCategories } from '../hooks/useWorkCategories';

export default function StructureUnit({ unit, onEditUnit, onDeleteUnit, onAddCategory, onEditCategory, onDeleteCategory, onAddTask }) {
  const [expanded, setExpanded] = useState(false);
  const unitId = useMemo(() => unit?.id ?? unit?._id ?? unit?.modularUnitId ?? unit?.modular_id, [unit]);
  const { categories, loading, error, refresh } = useWorkCategories(unitId, expanded);

  const title = useMemo(() => unit?.name || unit?.title || unit?.unitName || `Unit ${unitId}` , [unit, unitId]);

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded((e) => !e)} sx={{ mb: 1, boxShadow: 'none' }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
        sx={{
          backgroundColor: '#1976d2',
          color: 'white',
          borderRadius: 1,
          minHeight: 44,
          '& .MuiAccordionSummary-content': { alignItems: 'center', m: 0 },
        }}
      >
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <IconButton component="span" size="small" sx={{ color: 'white' }} onClick={(e) => { e.stopPropagation(); onEditUnit?.(unit); }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton component="span" size="small" sx={{ color: 'white' }} onClick={(e) => { e.stopPropagation(); onDeleteUnit?.(unit); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {/* Header inside details: Kategori Pekerjaan + Tambah Kategori */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Kategori Pekerjaan</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => onAddCategory?.(unitId)}
          >
            Tambah Kategori
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            Gagal memuat kategori pekerjaan. <Typography component="span" sx={{ ml: 1, cursor: 'pointer', textDecoration: 'underline' }} onClick={refresh}>Coba lagi</Typography>
          </Alert>
        ) : categories.length === 0 ? (
          <Alert 
            severity="info" 
            sx={{ 
              backgroundColor: '#e3f2fd',
              border: '1px solid #bbdefb',
              '& .MuiAlert-icon': {
                color: '#1976d2'
              }
            }}
          >
            Belum ada kategori pekerjaan. Klik "Tambah Kategori" untuk membuat kategori pekerjaan baru.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {categories.map((cat) => (
              <Box
                key={cat.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: '#8e24aa', // purple distinct from blue header
                  color: 'white',
                }}
              >
                <Typography sx={{ fontWeight: 500 }}>{cat.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {typeof cat.progress === 'number' && (
                    <Chip size="small" label={`${cat.progress}%`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                  )}
                  <IconButton size="small" sx={{ color: 'white' }} onClick={(e) => { e.stopPropagation(); onAddTask?.({ ...cat, unitName: title }); }} title="Tambah Pekerjaan">
                    <AddIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" sx={{ color: 'white' }} onClick={(e) => { e.stopPropagation(); onEditCategory?.(cat); }} title="Edit Kategori">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" sx={{ color: 'white' }} onClick={(e) => { e.stopPropagation(); onDeleteCategory?.(cat); }} title="Hapus Kategori">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
